/* eslint-disable no-console */
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, IconButton, Label, LABEL_TYPE, Tooltip } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { showToast, ToastType } from '@/helpers/toast';
import { LinkIcon, ThrashIconSmall } from '@/resources';
import { getTestId, TestIds } from '@/shared/test-ids';

type InvitationItemProps = {
  invitation: {
    id: number;
    email: string;
  };
  guestStreamInvitationData: string | null;
  isTooltipVisible: Record<number, boolean>;
  handleGetGuestStreamLink: (id: number) => void;
  handleTooltipClose: (id: number) => void;
  selectedInvitationId: number | null;
  handleOpenDeleteModal: (id: number, email: string) => void;
  deleteInvitation: (id: number) => void;
  isOpenDeleteModal: Record<number, boolean>;
  setIsOpenDeleteModal: Dispatch<SetStateAction<Record<number, boolean>>>;
  deletingInvitation: { id: number; name: string } | null;
  isLastItem?: boolean;
};

export const InvitationItem = ({
  invitation,
  guestStreamInvitationData,
  isTooltipVisible,
  handleGetGuestStreamLink,
  handleTooltipClose,
  selectedInvitationId,
  handleOpenDeleteModal,
  deleteInvitation,
  isOpenDeleteModal,
  setIsOpenDeleteModal,
  deletingInvitation,
  isLastItem
}: InvitationItemProps) => {
  const [t] = useTranslation('common');

  const handleCopyLiveStreamURL = async () => {
    try {
      if ('clipboard' in navigator) {
        await navigator.clipboard.writeText(guestStreamInvitationData ?? '');
        showToast(ToastType.SUCCESS, t('liveStreamURLSuccessfullyCopied'));
        return true;
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = guestStreamInvitationData ?? '';
        document.body.appendChild(textArea);
        textArea.select();

        const success = document.execCommand('copy');
        document.body.removeChild(textArea);

        return success;
      }
    } catch (err) {
      console.error('Unable to copy to clipboard', err);
      return false;
    }
  };

  return (
    <li
      data-testid={getTestId(TestIds.liveStream.guestInvitationItem, invitation.email)}
      className={`flex items-center ${!isLastItem && 'pb-1.5 md:pb-3'}`}
      key={invitation.id}>
      <Label text={invitation.email} type={LABEL_TYPE.PARAGRAPH2} />
      <Tooltip
        content={
          guestStreamInvitationData && (
            <>
              <div className='flex flex-row items-center md:hidden'>
                <Label
                  text={guestStreamInvitationData}
                  type={LABEL_TYPE.PARAGRAPH1}
                  className='max-w-[200px] truncate !text-xs md:max-w-[300px]'
                />
                <div className='mx-2 h-4 border-l border-solid border-secondary-blue-gray' />
                <Button variant={ButtonVariant.TERTIARY} handleOnClick={handleCopyLiveStreamURL}>
                  {t('copy')}
                </Button>
              </div>

              <div className='hidden flex-row items-center md:flex'>
                <Button
                  variant={ButtonVariant.TERTIARY}
                  handleOnClick={handleCopyLiveStreamURL}
                  className='overflow-hidden truncate whitespace-nowrap !text-xs !font-normal !text-secondary-gray'>
                  {t('copyLivestreamURL')}
                </Button>
              </div>
            </>
          )
        }
        isVisible={isTooltipVisible[invitation.id]}
        onClose={() => handleTooltipClose(invitation.id)}
        classes='w-fit ml-auto'
        position='bottom-0 right-[30px]'>
        <div
          data-testid={getTestId(TestIds.liveStream.guestInvitationLinkButton, invitation.email)}
          className='flex flex-row items-center border-none text-primary-blue'
          onClick={() => handleGetGuestStreamLink(invitation.id)}>
          <LinkIcon
            className={`w-6 ${
              selectedInvitationId === invitation.id ? 'text-primary-black' : 'text-secondary-gray'
            }`}
          />
        </div>
      </Tooltip>
      <IconButton
        dataTestId={getTestId(TestIds.liveStream.guestInvitationDeleteButton, invitation.email)}
        variant={IconButtonVariant.SECONDARY}
        handleOnClick={() => handleOpenDeleteModal(invitation.id, invitation.email)}
        className='ml-4'>
        <ThrashIconSmall fill={'var(--color-secondary-gray)'} />
      </IconButton>
      {isOpenDeleteModal[invitation.id] && (
        <ConfirmationModal
          onConfirm={() => deleteInvitation(Number(deletingInvitation?.id))}
          isOpen={isOpenDeleteModal[invitation.id]}
          onClose={() =>
            setIsOpenDeleteModal((prevState) => ({
              ...prevState,
              [Number(invitation.id)]: false
            }))
          }
          message={t('deleteMessage', {
            item: deletingInvitation?.name ?? ''
          })}
        />
      )}
    </li>
  );
};
